import { resolveMediaUrl } from "@/lib/ddfApi";
import { t } from "@/lib/i18n";
import { DirhamIcon, formatPrice } from "@/components/Price";
import { LANDING } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";

const PRODUCT_IMAGES = {
  LINDT: "https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&q=80",
  "DON JULIO":
    "https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=400&q=80",
  GUCCI:
    "https://images.unsplash.com/photo-1541643600914-78b084683601?w=400&q=80",
  APPLE:
    "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&q=80",
};

// Format ISO date string YYYY-MM-DD → "DD/MM/YYYY"
function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
}

// Corner badge shown on the top-right of a product image. For the known preset
// tags we render a branded icon; custom tags fall back to a colored text badge.
const CORNER_TAG_ICONS = {
  "OFFER": "/corner-offer.png",
  "OFFERS": "/corner-offer.png",
  "NEW": "/corner-new.png",
  "TRAVEL EXCLUSIVE": "/corner-travelexclusive.png",
  "FREE GIFTS": "/corner-freegifts.png",
  "LOVE EARTH": "/corner-loveearth.png",
};

const hasTextCornerTag = (item) =>
  !!item?.cornerTag && !CORNER_TAG_ICONS[item.cornerTag.trim().toUpperCase()];

export function CornerTag({ item }) {
  if (!item?.cornerTag) return null;
  const iconSrc = CORNER_TAG_ICONS[item.cornerTag.trim().toUpperCase()];
  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        alt={item.cornerTag}
        className="ddf-corner-tag-icon"
        data-testid="product-corner-tag"
      />
    );
  }
  return (
    <span
      className="ddf-corner-tag"
      style={{
        background: item.cornerTagColor || "#111827",
        color: item.cornerTagTextColor || "#ffffff",
      }}
      data-testid="product-corner-tag"
    >
      {item.cornerTag}
    </span>
  );
}

export function PromoThumb({ item, location, active, onClick }) {
  const img =
    resolveMediaUrl(item.image) ||
    PRODUCT_IMAGES[item.name?.toUpperCase()] ||
    "https://placehold.co/200x200/eeeeee/333?text=Product";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={
        location === "departures"
          ? LANDING.promoThumbDepartures
          : LANDING.promoThumbArrivals
      }
      className={`ddf-promo-thumb ${active ? "is-active" : ""} ${item.imageOnly ? "ddf-promo-thumb--image-only" : ""}`}
    >
      <div className={`ddf-promo-thumb-img ${hasTextCornerTag(item) ? "has-text-tag" : ""}`}>
        <CornerTag item={item} />
        <img src={img} alt={item.name} loading="lazy" />
      </div>
      {!item.imageOnly && (
        <div className="ddf-promo-thumb-meta">
          {item.header && (
            <span
              className="ddf-red-ribbon"
              data-len-long={String((item.header || "").length > 14)}
              title={item.header}
              style={{
                ...(item.ribbonColor ? { background: item.ribbonColor } : {}),
                ...(item.ribbonTextColor ? { color: item.ribbonTextColor } : {}),
              }}
            >
              {item.header}
            </span>
          )}
          <h4 className="ddf-promo-thumb-name">{item.name}</h4>
          <p className="ddf-promo-thumb-category">{item.category}</p>
          {item.priceAED ? (
            <p className="ddf-promo-thumb-price">
              <DirhamIcon /> {formatPrice(item.priceAED)}
            </p>
          ) : null}
        </div>
      )}
    </button>
  );
}

export function ProductCard({ item, onClick, lang }) {
  const img =
    resolveMediaUrl(item.image) ||
    PRODUCT_IMAGES[item.name?.toUpperCase()] ||
    "https://placehold.co/300x300/eeeeee/333?text=Product";
  const aed = Number(item.priceAED) || 0;
  const wasAED = Number(item.wasPrice) || 0;
  const usd = aed > 0 ? aed / 3.6 : 0;
  const wasUSD = wasAED > 0 ? wasAED / 3.6 : 0;
  return (
    <div
      className={`ddf-product-card ${item.imageOnly ? "ddf-product-card--image-only" : ""}`}
      data-testid={LANDING.productCard}
      onClick={() => onClick?.(item)}
      role="button"
    >
      <div className={`ddf-product-img-wrap ${hasTextCornerTag(item) ? "has-text-tag" : ""}`}>
        <CornerTag item={item} />
        <img src={img} alt={item.name} loading="lazy" />
      </div>
      {!item.imageOnly && (
        <div className="ddf-product-body">
          {item.header && (
            <span
              className="ddf-red-ribbon ddf-red-ribbon--card"
              style={{
                ...(item.ribbonColor ? { background: item.ribbonColor } : {}),
                ...(item.ribbonTextColor ? { color: item.ribbonTextColor } : {}),
              }}
            >
              {item.header}
            </span>
          )}
          <h3 className="ddf-product-name">{item.name}</h3>
          <p className="ddf-product-subtitle">
            {item.description || item.category}
          </p>
          <div
            className="ddf-price-block"
            style={aed > 0 ? undefined : { display: "none" }}
          >
            {wasAED > 0 && (
              <div className="ddf-was-row">
                <span className="ddf-was-label">{t(lang, "was")}</span>
                <span className="ddf-was-values">
                  <DirhamIcon /> {formatPrice(wasAED)}
                  {wasUSD > 0 && (
                    <>
                      {" "}
                      - {t(lang, "in_usd")} {formatPrice(wasUSD)}
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="ddf-price-current">
              <div className="ddf-price-label">{t(lang, "price")}</div>
              <div className="ddf-price-main">
                <DirhamIcon /> {formatPrice(aed)}
              </div>
              {usd > 0 && (
                <div className="ddf-price-usd">
                  {t(lang, "in_usd")} {formatPrice(usd)}
                </div>
              )}
            </div>
          </div>
          <div className="ddf-product-valid">
            {item.validUntil
              ? `Valid until ${formatDate(item.validUntil)}`
              : "Valid until 31/05/2026"}
          </div>
          {item.terminal && (
            <div className="ddf-product-terminal" data-testid="product-terminal">
              {item.terminal}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DrawCard({ title, subtitle, image, link, testId, bgColor = "#222", entry }) {
  const handleClick = () => {
    if (entry) trackEvent("draw_button_click", { entry, title, subtitle });
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  };
  return (
    <div
      className="ddf-draw-card"
      data-testid={testId}
      onClick={handleClick}
      role="button"
    >
      <div className="ddf-draw-card-img-wrap" style={{ backgroundColor: bgColor }}>
        <img src={image} alt={title} loading="lazy" />
      </div>
      <div className="ddf-draw-card-text">
        <h3 className="ddf-draw-card-title">{title}</h3>
        <p className="ddf-draw-card-subtitle">{subtitle}</p>
      </div>
    </div>
  );
}
